<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div :class="bodyClassName">
      <div class="row">
        <div class="col-md-12">
          <h3>
            <i class="fa fa-fw fa-barcode"></i> {{ _('Stock scanner') }}
          </h3>
          <p>{{ _('Scan EAN codes while this view is open to adjust the stock balance.') }}</p>
          <p>{{ _('You can also manually enter an EAN code to the input field.') }}</p>
          <div class="form-inline">
            <input class="form-control" type="text" v-model="code" /><button class="btn btn-default" type="button" v-on:click="addEan()">{{ _('Add') }}</button>
          </div>
        </div>
      </div>
      <div class="row" v-if="items.length">
        <div class="col-md-12">
          <h3>{{ _('Products') }}</h3>
          <table v-for="item in items">
            <tbody>
              <tr>
                <td colspan="5"><h4>{{ item.product.name }}</h4></td>
              </tr>
              <tr v-for="operation in item.operations">
                <td style="width: 250px; padding-left: 20px">
                  <select class="form-control" v-model="operation.resource_id" :disabled="operation.done">
                    <option :value="null" v-if="item.possibleResources && item.possibleResources.length > 1">{{ _('Select a resource') }}</option>
                    <option v-for="resource in item.possibleResources" :value="resource.id">{{ resource.name }}</option>
                  </select>
                </td>
                <td style="width: 100px; padding-left: 10px">{{ getWarehouseCount(item, operation) }}</td>
                <td style="width: 100px; padding-left: 10px">{{ _('Add') }}</td>
                <td><input type="number" data-android-inputmode="decimal" class="form-control" style="width: 100px" v-model="operation.count" :disabled="operation.done" /></td>
                <td v-if="!operation.done"><button class="btn btn-xs btn-danger" v-on:click="removeOperation(item, operation)"><i class="fa fa-fw fa-times"></i></button></td>
                <td v-else-if="operation.processing">{{ _('Processing') }}</td>
                <td v-else-if="operation.done">{{ _('Done') }}</td>
                <td v-else-if="operation.error">{{ _('Error') }}</td>
              </tr>
            </tbody>
          </table>
          <button class="btn btn-success" type="button" v-on:click="applyStockBalance" :disabled="!isApplyButtonEnabled"><i class="fa fa-fw fa-play"></i> {{ _('Apply changes') }}</button>
          <button class="btn btn-default" type="button" v-on:click="clearDone" v-if="hasDoneOperations"><i class="fa fa-fw fa-ban"></i> {{ _('Clear completed') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import components from '~/lib/components'
import formats from '~/lib/formats'
import products from '~/lib/products'
import { mapGetters } from 'vuex'

export default {
  data () {
    return {
      details: {
        product: null
      },
      items: [],
      code: ''
    }
  },
  mixins: [components, formats, products],
  computed: {
    ...mapGetters(['locale', 'numberFormat']),
    shortLocale () {
      return this.locale.split('_')[0]
    },
    isApplyButtonEnabled () {
      return this.items.filter(i => i.operations && i.operations.find(o => !o.resource_id)).length === 0 && this.items.filter(i => i.operations && i.operations.find(o => !o.done)).length > 0
    },
    hasDoneOperations () {
      return this.items.filter(i => i.operations && i.operations.filter(o => o.done).length).length > 0
    },
    vats () {
      return this.settings && this.settings.vats ? [...new Set([...this.settings.vats.split(';').sort((a, b) => b - a).map(v => parseFloat(v)), 0])] : [0]
    },
    vatOptions () {
      return this.vats.map(v => { return { name: (parseInt(v, 10) != parseFloat(v) ? this.formatVat(v, this.numberFormat) : v) + ' %', value: v }} )
    }
  },
  methods: {
    getWarehouseCount (item, operation) {
      if (!item || !operation || !operation.resource_id) {
        return ''
      }
      const resource = item.possibleResources.find(r => r.id === operation.resource_id)
      if (resource) {
        return resource.warehouse_count + ' ' + this._(resource.unit)
      }
      return ''
    },
    async addEan(code, isNew) {
      let response
      if (!code) {
        code = this.code
      }
      if (!code) {
        return
      }
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/products.json?ean=' + encodeURIComponent(code))
      } catch(e) {
        if (e && e.response && e.response.status === 404) {
          return this.linkProductDialog(code)
        }
        this.showError(e)
        return
      }
      if (response && response.data) {
        const product = response.data
        this.productScanned({
          shopId: this.shopId,
          id: product.id,
          product: product,
          count: 1,
          isNew: isNew
        })
      }
    },
    async linkProductDialog(code) {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/products.json?sSearch_3=0-0-all&iSortingCols=1&iSortCol_0=2&sSortDir_0=asc')
      } catch(e) {
        this.showError(e)
        return
      }
      const allProducts = response.data
      this.showDialog({
        title: this._('Link product'),
        width: 800,
        buttons: [
          {
            title: this._('Close'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('New product'),
            class: 'success',
            icon: 'plus',
            handler: () => {
              setTimeout(() => {
                this.createNewProductDialog(code)
              }, 100)
            }
          },
          {
            title: this._('OK'),
            class: 'success',
            icon: 'check',
            requiredFields: ['product'],
            handler: (data) => {
              const product = allProducts.find(p => parseInt(p.id, 10) === parseInt(data.product.value, 10))
              if (product) {
                this.linkProduct(product, code)
              }
            }
          }
        ],
        fields: [
          {
            title: this._('Product'),
            type: 'selectize',
            name: 'product',
            options: allProducts.filter(p => !p.ean).map(prod => { return { value: prod.id, name: prod.id + ': ' + prod.name }})
          }
        ]
      })
    },
    async createNewProductDialog (code) {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/categories.json?iSortingCols=1&iSortCol_0=1&sSortDir_0=asc')
      } catch(e) {
        this.showError(e)
        return
      }
      const allCategories = response.data
      this.showDialog({
        title: this._('New product'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Save'),
            class: 'success',
            icon: 'save',
            requiredFields: ['name', 'ean'],
            handler: (data) => {
              this.createNewProduct(data)
            }
          }
        ],
        fields: [
          {
            title: this._('Name'),
            type: 'text',
            name: 'name'
          },
          {
            title: this._('Category'),
            type: 'selectize',
            name: 'category',
            options: allCategories.map(cat => { return { value: cat.id, name: cat.id + ': ' + cat.name }})
          },
          {
            title: this._('Barcode'),
            type: 'text',
            name: 'ean',
            defaultValue: code
          },
          {
            title: this._('Price'),
            type: 'number',
            name: 'price',
          },
          {
            title: this._('VAT'),
            type: 'select',
            name: 'vat',
            options: this.vatOptions,
            defaultValue: this.vats[0]
          },
        ]
      })
    },
    async linkProduct (product, code) {
      let response
      try {
        response = await axios.patch(this.baseDomain + this.shopId + '/products/' + product.id + '.json?apikey=' + this.apiKey, { id: product.id, ean: code })
      } catch(e) {
        this.showError(e)
        return
      }
      this.addEan(code, false)
    },
    async createNewProduct (data) {
      if (data.category) {
        data.category = data.category.value
      }
      let response
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/products.json?apikey=' + this.apiKey, data)
      } catch(e) {
        this.showError(e)
        return
      }
      this.addEan(response.data, true)
    },
    productScanned (message) {
      const product = message.product
      if (parseInt(product.type) !== this.TYPE_BUYABLE) {
        return this.showError(this._('The scanned product does not have a stock balance.'))
      }
      this.showNotification(this._('Product') + ': ' +  product.name)
      let item = this.items.find(i => i.product_id === product.id)
      if (item) {
        if (item.operations.length === 1 && item.possibleResources.length === 1 && !item.operations[0].done) {
          item.operations[0].count++
        } else {
          item.operations.push({
            resource_id: item.possibleResources.length !== 1 ? null : item.possibleResources[0].id,
            count: 1,
            processing: false,
            done: false,
            error: false
          })
        }
      } else {
        let possibleResources = this.getPossibleResources(product)
        item = {
          product_id: product.id,
          product: product,
          operations: [{
            resource_id: possibleResources.length !== 1 ? null : possibleResources[0].id,
            count: 1,
            processing: false,
            done: false,
            error: false
          }],
          possibleResources: possibleResources,
        }
        if (message.isNew) {
          item.isNew = message.isNew
        }
        this.items.push(item)
      }
    },
    getPossibleResources (product) {
      const ret = []
      if (product.sharedResource && product.sharedResource.length) {
        ret.push(...product.sharedResource)
      }
      if (product.sharedResourcepool && product.sharedResourcepool.length) {
        product.sharedResourcepool.forEach(pool => {
          if (pool.sharedResource && pool.sharedResource.length) {
            ret.push(...pool.sharedResource)
          }
          if (pool.sharedResourcepool && pool.sharedResourcepool.length) {
            product.sharedResourcepool.forEach(subpool => {
              if (subpool.sharedResource && subpool.sharedResource.length) {
                ret.push(...subpool.sharedResource)
              }
            })
          }
        })
      }
      return ret.filter(r => r.displayedtype !== 'openinghours')
    },
    removeOperation (item, operation) {
      item.operations = item.operations.filter(o => o !== operation)
      if (!item.operations.length) {
        this.items = this.items.filter(i => i !== item)
      }
    },
    async applyStockBalance () {
      for (var item of this.items) {
        for (var operation of item.operations) {
          if (operation.done) {
            continue
          }
          try {
            operation.processing = true
            const data = {
              id: operation.resource_id,
              count: operation.count,
              originalcount: item.isNew ? 1 : 0 // We can cheat here, this just adds count items. For newly created items, let's use 1 as the stock balance so that we don't unintentionally add extra capacity
            }
            await axios.patch(this.baseDomain + this.shopId + '/resources/' + operation.resource_id + '.json?apikey=' + this.apiKey, data)
            operation.done = true
            operation.error = false
          } catch(e) {
            operation.error = true
          } finally {
            operation.processing = false
          }
        }
      }
    },
    clearDone () {
      this.items.forEach(item => {
        const done = item.operations.filter(o => o.done)
        done.forEach(operation => {
          this.removeOperation(item, operation)
        })
      })
    }
  },
  async mounted () {
    if(!this.events) {
      let events = {}
      events['stock.productScanned'] = async (message) => {
        this.addEan(message.code)
      }
      this.events = events
    }
    this.enableEventBus()
  }  
}

</script>

<style scoped>
table {
  margin-bottom: 15px;
}
td {
  padding: 4px;
}
</style>